import { useQuery } from "@tanstack/react-query";
import { Api, ApiError } from "../api/client";
import { Activity, AlertCircle, CheckCircle2, Send, Newspaper, ExternalLink } from "lucide-react";
import clsx from "clsx";

const CHANNEL_LABEL: Record<string, string> = {
  telegram: "Telegram",
  vk: "VK",
  ok: "OK",
  max: "MAX",
  dzen: "Дзен",
};

export function Dashboard() {
  const ready = useQuery({
    queryKey: ["ready"],
    queryFn: Api.ready,
    refetchInterval: 15_000,
  });

  const today = useQuery({
    queryKey: ["status-today"],
    queryFn: Api.statusToday,
    refetchInterval: 30_000,
  });

  const isReady = ready.data?.status === "ready" && ready.data?.db === "up";

  const social = today.data?.social ?? [];
  const dzen = today.data?.dzen ?? [];
  // Combined-режим: канонический Дзен-вывод — один объединённый пост дня (sign_code='all').
  const combinedPub = dzen.find(d => d.signCode === "all");
  const perSignLegacy = dzen.filter(d => d.signCode !== "all");

  // groupedBy channel
  const socialByChannel: Record<string, typeof social> = {};
  for (const row of social) {
    const k = row.channelCode.toLowerCase();
    (socialByChannel[k] ??= []).push(row);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Дашборд</h1>
          <p className="text-white/50 text-sm mt-1">
            Сегодня — {today.data?.date ?? "…"}
          </p>
        </div>
        <div className={clsx(
          "flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm",
          isReady
            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
            : "bg-rose-500/10 border-rose-500/30 text-rose-300"
        )}>
          {isReady ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {isReady ? "Сервис здоров" : "Проблема"}
        </div>
      </div>

      {today.error instanceof ApiError && today.error.status === 401 && (
        <div className="card p-4 border-rose-500/30 bg-rose-500/5 text-rose-200 text-sm">
          API key не подходит. Выйди и введи снова.
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Send className="text-astro-violet" size={22} />}
          label="Социалок опубликовано"
          value={`${social.filter(s => /publish/i.test(s.status)).length} / ${social.length}`}
          hint={Object.keys(socialByChannel).map(k => CHANNEL_LABEL[k] ?? k).join(", ")}
        />
        <StatCard
          icon={<Newspaper className="text-astro-gold" size={22} />}
          label="Дзен — пост дня"
          value={combinedPub?.status === "published" ? "опубликован" : "—"}
          hint={combinedPub
            ? (combinedPub.status === "published" ? "1 объединённый пост" : combinedPub.status)
            : "ещё не публиковали"}
        />
        <StatCard
          icon={<Activity className="text-emerald-400" size={22} />}
          label="DB"
          value={ready.data?.db ?? "?"}
        />
        <StatCard
          icon={<CheckCircle2 className={isReady ? "text-emerald-400" : "text-rose-400"} size={22} />}
          label="Status"
          value={ready.data?.status ?? "?"}
        />
      </section>

      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Социалки за сегодня</h2>
        {social.length === 0 ? (
          <p className="text-white/40 text-sm">Пока никаких публикаций.</p>
        ) : (
          <div className="space-y-2">
            {social.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="font-medium w-20">{CHANNEL_LABEL[s.channelCode.toLowerCase()] ?? s.channelCode}</span>
                  <StatusBadge status={s.status} />
                  {s.externalUrl && (
                    <a href={s.externalUrl} target="_blank" rel="noreferrer"
                       className="text-astro-violet hover:underline text-sm">{s.externalPostId}</a>
                  )}
                  {!s.externalUrl && s.externalPostId && (
                    <span className="text-white/50 text-sm font-mono">{s.externalPostId}</span>
                  )}
                </div>
                <span className="text-xs text-white/40">
                  {s.publishedAt ? new Date(s.publishedAt).toLocaleTimeString("ru-RU") : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Дзен за сегодня — пост дня</h2>
        {!combinedPub ? (
          <p className="text-white/40 text-sm">Объединённый пост дня ещё не публиковался.</p>
        ) : (
          <div className="bg-black/20 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium">Пост дня (все знаки)</span>
              <StatusBadge status={combinedPub.status} />
              {combinedPub.postUrl ? (
                <a href={combinedPub.postUrl} target="_blank" rel="noreferrer"
                   className="text-astro-violet hover:underline text-sm inline-flex items-center gap-1">
                  открыть пост <ExternalLink size={12} />
                </a>
              ) : combinedPub.postId && combinedPub.status !== "published" ? (
                <a href={`https://dzen.ru/profile/editor/daily_horoscop/publication/${combinedPub.postId}/edit`}
                   target="_blank" rel="noreferrer"
                   className="text-astro-violet/70 hover:text-astro-violet text-xs">draft в Studio</a>
              ) : null}
              {combinedPub.lastErrorKind && (
                <span className="text-rose-300/60 text-xs">{combinedPub.lastErrorKind}</span>
              )}
            </div>
            <span className="text-xs text-white/40">
              {combinedPub.publishedAt ? new Date(combinedPub.publishedAt).toLocaleTimeString("ru-RU") : "—"}
            </span>
          </div>
        )}
        {perSignLegacy.length > 0 && (
          <p className="text-xs text-white/30 mt-3">
            + {perSignLegacy.length} пер-знак статей (легаси-режим; основной вывод — пост дня выше)
          </p>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/50 text-sm">{label}</span>
        {icon}
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
      {hint && <div className="text-xs text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s.includes("publish")) return <span className="badge-ok">опубликовано</span>;
  if (s.includes("fail") || s.includes("error")) return <span className="badge-fail">ошибка</span>;
  if (s.includes("queue") || s.includes("draft") || s.includes("ready")) return <span className="badge-pending">{status}</span>;
  return <span className="badge-pending">{status}</span>;
}
